import { useQuery, useMutation, useQueryClient, keepPreviousData } from "@tanstack/react-query";
import UserNotification from "util/UserNotification";
import { useDashboardContext } from "security-app/components/Investigations/Dashboard";
import { defaultOnError } from "util/conditional/onError";
import { slicesQueryKeyForColumn } from "components/common/PaginatedEntityTable/slicing/queryKeys";
import {
  investigationDetailsQueryKey,
  investigationDrawerEventsQueryKey,
  investigationDrawerLogsQueryKey
} from "security-app/hooks/queryKeys";
import {
  fetchInvestigations,
  getInvestigationDetails,
  newInvestigation,
  updateInvestigations,
  archiveInvestigation,
  fetchPriorities,
  fetchStatuses,
  fetchUsers,
  fetchTeams,
  addNote,
  deleteNote,
  editNote,
  addEvidence,
  bulkAddEvidence,
  addPriority,
  updatePriority,
  reprioritizePriorities,
  deletePriority,
  addStatus,
  updateStatus,
  deleteStatus,
  removeEvidence,
  bulkArchiveInvestigation,
  deleteInvestigation,
  bulkDeleteInvestigations,
  setDefaultPriority,
  removeDefaultPriority,
  setDefaultStatus,
  removeDefaultStatus,
  enableNotifications,
  fetchInvestigationsConfig,
  fetchInvestigationsFilterOptions
} from "./api/investigationsAPI";
const ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN = "associated_investigations";
export function useGetInvestigations({ page, perPage, query = void 0, orderBy = void 0, direction = void 0, filters = void 0 }, archived, userTimezone) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigations", page, perPage, query, orderBy, direction, filters, userTimezone, archived],
    queryFn: () => defaultOnError(
      fetchInvestigations(page, perPage, query, orderBy, direction, filters, archived, userTimezone),
      "Error fetching investigations"
    ),
    retry: 2,
    placeholderData: keepPreviousData
  });
  return {
    loadingInvestigations: isLoading,
    investigations: isLoading ? [] : data == null ? void 0 : data.investigations,
    pagination: {
      page: (data == null ? void 0 : data.page) || page,
      perPage: (data == null ? void 0 : data.per_page) || perPage,
      total: (data == null ? void 0 : data.total) || 0,
      grandTotal: (data == null ? void 0 : data.grand_total) || 0,
      count: (data == null ? void 0 : data.count) || 0
    }
  };
}
export function useInvestigationsFilterOptions({ isArchived, fields }) {
  const { data, isLoading } = useQuery({
    queryKey: ["investigations-filter-options", isArchived, fields],
    queryFn: () => defaultOnError(
      fetchInvestigationsFilterOptions({
        fields,
        archived: isArchived
      }),
      "Error fetching investigations filter options"
    )
  });
  return {
    filterOptions: data,
    fetchingInvestigationsFilterOptions: isLoading
  };
}
export function useGetInvestigationDetails(invId, loadCompleteLogAndEvents = false) {
  const { data, isLoading, isError } = useQuery({
    queryKey: investigationDetailsQueryKey(invId, loadCompleteLogAndEvents),
    queryFn: () => {
      var _a;
      try {
        return getInvestigationDetails(invId, loadCompleteLogAndEvents);
      } catch (error) {
        const currentInvestigationId = (_a = localStorage.getItem("currentInvestigationId")) != null ? _a : void 0;
        if (currentInvestigationId === invId) {
          UserNotification.warning("The investigation you were viewing has been deleted");
        } else if (error.status === 404) {
          UserNotification.error(
            `Unable to find Investigation with id <${invId}>, please ensure it wasn't deleted.`,
            "Could not retrieve Investigation"
          );
        } else {
          UserNotification.error(error.message);
        }
        throw error;
      }
    },
    retry: 2,
    enabled: !!invId
  });
  return {
    loadingInvestigationDetails: isLoading,
    fetchError: isError,
    currentInvestigation: data
  };
}
export function useNewInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: newInvestigation,
    onSuccess: () => {
      UserNotification.success("New Investigation created");
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { createInvestigation: mutateAsync, creatingInvestigation: isLoading };
}
export function useUpdateInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateInvestigations,
    onSuccess: (data) => {
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success("Investigations updated successfully");
      }
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    updateInvestigations: mutateAsync,
    updatingInvestigations: isLoading
  };
}
export function useArchiveInvestigation() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: archiveInvestigation,
    onSuccess: () => {
      UserNotification.success("Investigation archive status updated successfully");
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    archiveInvestigation: mutateAsync,
    archivingInvestigation: isLoading
  };
}
export function useBulkArchiveInvestigation() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkArchiveInvestigation,
    onSuccess: () => {
      UserNotification.success("Investigations archive status updated successfully");
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    bulkArchiveInvestigation: mutateAsync,
    archivingInvestigations: isLoading
  };
}
export function useDeleteInvestigation() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteInvestigation,
    onSuccess: () => {
      UserNotification.success("Investigation deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    deleteInvestigation: mutateAsync,
    deletingInvestigation: isLoading
  };
}
export function useBulkDeleteInvestigations() {
  const queryClient = useQueryClient();
  const { forceRefreshDashboard } = useDashboardContext();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkDeleteInvestigations,
    onSuccess: () => {
      UserNotification.success("Investigations deleted successfully");
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      forceRefreshDashboard();
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return {
    bulkDelete: mutateAsync,
    bulkDeleting: isLoading
  };
}
export function useAddEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addEvidence,
    onSuccess: async (data) => {
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success("Evidence added to investigation");
      }
      await new Promise((resolve) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: investigationDetailsQueryKey()
          });
          queryClient.invalidateQueries({
            queryKey: slicesQueryKeyForColumn(ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN)
          });
          resolve();
        }, 1e3);
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addEvidence: mutateAsync, addingEvidence: isLoading };
}
const invalidateEntities = (queryClient, payload) => {
  const investigations = payload == null ? void 0 : payload.investigation_ids;
  const events = payload == null ? void 0 : payload.events;
  if (investigations == null ? void 0 : investigations.length) {
    investigations.forEach(
      (investigation) => queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey(investigation)
      })
    );
  }
  if (events == null ? void 0 : events.length) {
    events.forEach(
      (event) => queryClient.invalidateQueries({
        queryKey: ["get-security-events", event]
      })
    );
  }
  queryClient.invalidateQueries({
    queryKey: slicesQueryKeyForColumn(ASSOCIATED_INVESTIGATIONS_SLICE_COLUMN)
  });
};
export function useBulkAddEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: bulkAddEvidence,
    onSuccess: async (data, variables) => {
      if (data.errors.length > 0) {
        const error = { type: "warning", message: `${data.errors}` };
        UserNotification[error.type](error.message);
      } else {
        UserNotification.success("Evidence added to investigation");
      }
      await new Promise((resolve) => {
        setTimeout(() => {
          invalidateEntities(queryClient, variables.payload);
          resolve();
        }, 1e3);
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { bulkAddEvidence: mutateAsync, addingEvidence: isLoading };
}
export function useRemoveEvidence() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeEvidence,
    onSuccess: async (_data, variables) => {
      queryClient.invalidateQueries({
        queryKey: investigationDrawerEventsQueryKey(variables.invId)
      });
      queryClient.invalidateQueries({
        queryKey: investigationDrawerLogsQueryKey(variables.invId)
      });
      UserNotification.success("Evidence removed from investigation");
      await new Promise((resolve) => {
        setTimeout(() => {
          queryClient.invalidateQueries({
            queryKey: investigationDetailsQueryKey()
          });
          resolve();
        }, 1e3);
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeEvidence: mutateAsync, removingEvidence: isLoading };
}
export function useAddNote() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addNote,
    onSuccess: () => {
      UserNotification.success("New note added to investigation");
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addNote: mutateAsync, addingNote: isLoading };
}
export function useEditNote() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: editNote,
    onSuccess: () => {
      UserNotification.success("Note edited");
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { editNote: mutateAsync, editingNote: isLoading };
}
export function useDeleteNote() {
  const queryClient = useQueryClient();
  const { mutateAsync } = useMutation({
    mutationFn: deleteNote,
    onSuccess: () => {
      UserNotification.success("Note deleted from investigation");
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteNote: mutateAsync };
}
export function useGetPriorities(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigation-priorities"],
    queryFn: () => defaultOnError(fetchPriorities(), "Error fetching priorities"),
    retry: 2,
    enabled: execute
  });
  return {
    priorities: (data || []).sort((a, b) => a.priority - b.priority),
    loadingPriorities: isLoading
  };
}
export function useGetStatuses(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-statuses"],
    queryFn: () => defaultOnError(fetchStatuses(), "Error fetching statuses"),
    retry: 2,
    enabled: execute
  });
  return {
    statuses: data || [],
    loadingStatuses: isLoading
  };
}
export function useGetUsers(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-users"],
    queryFn: () => defaultOnError(fetchUsers(), "Error fetching users"),
    retry: 2,
    enabled: execute
  });
  const { users } = data || { users: [] };
  return {
    users,
    loadingUsers: isLoading
  };
}
export function useGetTeams(execute = true) {
  const { data, isLoading } = useQuery({
    queryKey: ["get-all-teams"],
    queryFn: () => defaultOnError(fetchTeams(), "Error fetching teams"),
    retry: 2,
    enabled: execute
  });
  const { teams } = data || { teams: [] };
  return {
    teams,
    loadingTeams: isLoading
  };
}
export function useAddPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addPriority,
    onSuccess: () => {
      UserNotification.success("New priority tag added");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addPriority: mutateAsync, addingPriority: isLoading };
}
export function useUpdatePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updatePriority,
    onSuccess: () => {
      UserNotification.success("Priority tag updated");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updatePriority: mutateAsync, updatingPriority: isLoading };
}
export function useReprioritizePriorities() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: reprioritizePriorities,
    onSuccess: () => {
      UserNotification.success("Priority tags updated");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { reprioritizePriorities: mutateAsync, reprioritizingPriorities: isLoading };
}
export function useDeletePriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deletePriority,
    onSuccess: () => {
      UserNotification.success("Priority tag deleted");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deletePriority: mutateAsync, deletingPriority: isLoading };
}
export function useAddStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: addStatus,
    onSuccess: () => {
      UserNotification.success("New status tag added");
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { addStatus: mutateAsync, addingStatus: isLoading };
}
export function useUpdateStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: updateStatus,
    onSuccess: () => {
      UserNotification.success("Status tag updated");
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { updateStatus: mutateAsync, updatingStatus: isLoading };
}
export function useDeleteStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: deleteStatus,
    onSuccess: () => {
      UserNotification.success("Status tag deleted");
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
      queryClient.invalidateQueries({
        queryKey: ["get-investigations"]
      });
      queryClient.invalidateQueries({
        queryKey: investigationDetailsQueryKey()
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { deleteStatus: mutateAsync, deletingStatus: isLoading };
}
export function useSetDefaultPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultPriority,
    onSuccess: () => {
      UserNotification.success("Default priority set");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { setDefault: mutateAsync, settingDefault: isLoading };
}
export function useRemoveDefaultPriority() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultPriority,
    onSuccess: () => {
      UserNotification.success("Default priority removed");
      queryClient.invalidateQueries({
        queryKey: ["get-investigation-priorities"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeDefault: mutateAsync, removingDefault: isLoading };
}
export function useSetDefaultStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: setDefaultStatus,
    onSuccess: () => {
      UserNotification.success("Default status set");
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { setDefault: mutateAsync, settingDefault: isLoading };
}
export function useRemoveDefaultStatus() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: removeDefaultStatus,
    onSuccess: () => {
      UserNotification.success("Default status removed");
      queryClient.invalidateQueries({
        queryKey: ["get-all-statuses"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { removeDefault: mutateAsync, removingDefault: isLoading };
}
export function useEnableNotifications() {
  const queryClient = useQueryClient();
  const { mutateAsync, isPending: isLoading } = useMutation({
    mutationFn: enableNotifications,
    onSuccess: (response) => {
      UserNotification.success(`${response.enabled ? "Enabled" : "Disabled"} investigation notifications`);
      queryClient.invalidateQueries({
        queryKey: ["get-investigations-config"]
      });
    },
    onError: (error) => UserNotification.error(error.message)
  });
  return { enableNotifications: mutateAsync, enablingNotifications: isLoading };
}
export function useGetInvestigationsConfig() {
  const { data, isLoading } = useQuery({
    queryKey: ["get-investigations-config"],
    queryFn: () => defaultOnError(fetchInvestigationsConfig(), "Error fetching investigations config"),
    retry: 2
  });
  return {
    config: data || {
      enable_assignment_notifications: false,
      default_priority: "",
      default_status: ""
    },
    loadingConfig: isLoading
  };
}
